'use client';

import { useEffect, useRef, useState } from 'react';
import { roundRectPath, wrapText } from './canvas-mockup';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

// A Reddit post is not a chat thread, so this does not reuse BrowserChatGenerator: the
// recognisable shape is a card with the subreddit line, a large title, body text, and the
// vote / comment / share row underneath. Getting that row right is most of the job, because
// it is the part people recognise before they read a word.

type RedditTheme = 'light' | 'dark';

const palette: Record<
  RedditTheme,
  {
    bg: string;
    card: string;
    border: string;
    title: string;
    body: string;
    meta: string;
    pill: string;
    pillText: string;
    voteBg: string;
  }
> = {
  light: {
    bg: '#dae0e6',
    card: '#ffffff',
    border: '#cccccc',
    title: '#1a1a1b',
    body: '#1a1a1b',
    meta: '#7c7c7c',
    pill: '#f6f7f8',
    pillText: '#878a8c',
    voteBg: '#f8f9fa',
  },
  dark: {
    bg: '#030303',
    card: '#1a1a1b',
    border: '#343536',
    title: '#d7dadc',
    body: '#d7dadc',
    meta: '#818384',
    pill: '#272729',
    pillText: '#818384',
    voteBg: '#161617',
  },
};

const ORANGE = '#ff4500';

function formatScore(value: number) {
  // Reddit switches to "12.3k" above a thousand, and that abbreviation is part of what makes
  // the number read as a Reddit score rather than a plain integer.
  if (Math.abs(value) < 1000) return String(value);
  return `${(value / 1000).toFixed(1).replace(/\.0$/, '')}k`;
}

function drawArrow(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
  up: boolean,
  color: string,
) {
  ctx.fillStyle = color;
  ctx.beginPath();
  if (up) {
    ctx.moveTo(cx, cy - size);
    ctx.lineTo(cx + size, cy + size * 0.5);
    ctx.lineTo(cx - size, cy + size * 0.5);
  } else {
    ctx.moveTo(cx, cy + size);
    ctx.lineTo(cx + size, cy - size * 0.5);
    ctx.lineTo(cx - size, cy - size * 0.5);
  }
  ctx.closePath();
  ctx.fill();
}

function drawBubble(ctx: CanvasRenderingContext2D, x: number, y: number, color: string) {
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.6;
  roundRectPath(ctx, x, y - 8, 15, 12, 3);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(x + 4, y + 4);
  ctx.lineTo(x + 4, y + 8);
  ctx.lineTo(x + 8, y + 4);
  ctx.stroke();
}

export default function BrowserRedditPostGenerator() {
  const [subreddit, setSubreddit] = useState('r/AskReddit');
  const [author, setAuthor] = useState('u/throwaway_2291');
  const [age, setAge] = useState('7 hr. ago');
  const [title, setTitle] = useState('What is a small tool you use that feels like a superpower?');
  const [body, setBody] = useState(
    'I keep finding things that do one job and do it instantly, no account, no upload. Curious what everyone else is quietly relying on.',
  );
  const [score, setScore] = useState(12400);
  const [comments, setComments] = useState(843);
  const [mode, setMode] = useState<RedditTheme>('light');
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const p = palette[mode];
    const scale = 2;
    const width = 640;
    const pad = 16;
    const voteW = 44;
    const contentX = voteW + pad;
    const contentW = width - contentX - pad;

    ctx.font = '700 20px system-ui, sans-serif';
    const titleLines = wrapText(ctx, title.trim() || 'Post title', contentW);
    ctx.font = '400 15px system-ui, sans-serif';
    const bodyLines = body.trim()
      ? body
          .split('\n')
          .filter((line) => line.trim() !== '')
          .flatMap((line) => wrapText(ctx, line.trim(), contentW))
      : [];

    const metaH = 30;
    const titleH = titleLines.length * 26;
    const bodyH = bodyLines.length * 22;
    const actionsH = 40;
    // The vote rail needs ~90px of its own. A one-line post with no body produces a card shorter
    // than that, and the arrows end up crowding the comment row, so the card floors at the rail.
    const cardH = Math.max(pad + metaH + titleH + (bodyH ? bodyH + 10 : 0) + actionsH + pad, 110);
    const totalH = cardH + pad * 2;

    canvas.width = width * scale;
    canvas.height = totalH * scale;
    ctx.scale(scale, scale);

    ctx.fillStyle = p.bg;
    ctx.fillRect(0, 0, width, totalH);

    // The card
    ctx.fillStyle = p.card;
    roundRectPath(ctx, pad, pad, width - pad * 2, cardH, 6);
    ctx.fill();
    ctx.strokeStyle = p.border;
    ctx.lineWidth = 1;
    ctx.stroke();

    // Vote rail down the left edge, the way old.reddit and the desktop card both draw it.
    ctx.fillStyle = p.voteBg;
    roundRectPath(ctx, pad + 1, pad + 1, voteW - 4, cardH - 2, 6);
    ctx.fill();

    const railX = pad + voteW / 2;
    drawArrow(ctx, railX, pad + 26, 7, true, ORANGE);
    ctx.fillStyle = p.title;
    ctx.font = '700 13px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(formatScore(score), railX, pad + 48);
    ctx.textAlign = 'left';
    drawArrow(ctx, railX, pad + 66, 7, false, p.meta);

    let y = pad + pad + 4;

    // Subreddit line: bold subreddit, then the muted author and age.
    ctx.font = '700 13px system-ui, sans-serif';
    ctx.fillStyle = p.title;
    const sub = subreddit.trim() || 'r/subreddit';
    ctx.fillText(sub, contentX, y);
    const subW = ctx.measureText(sub).width;
    ctx.font = '400 13px system-ui, sans-serif';
    ctx.fillStyle = p.meta;
    ctx.fillText(
      `  •  Posted by ${author.trim() || 'u/someone'} ${age.trim()}`,
      contentX + subW,
      y,
    );

    y += 28;
    ctx.font = '700 20px system-ui, sans-serif';
    ctx.fillStyle = p.title;
    for (const line of titleLines) {
      ctx.fillText(line, contentX, y);
      y += 26;
    }

    if (bodyLines.length) {
      y += 10;
      ctx.font = '400 15px system-ui, sans-serif';
      ctx.fillStyle = p.body;
      for (const line of bodyLines) {
        ctx.fillText(line, contentX, y);
        y += 22;
      }
    }

    // Action row. The comment count with a speech bubble is the single most recognisable
    // element of a Reddit card, so it is drawn rather than written out as plain text.
    y += 22;
    drawBubble(ctx, contentX, y, p.pillText);
    ctx.font = '700 12px system-ui, sans-serif';
    ctx.fillStyle = p.pillText;
    ctx.fillText(`${formatScore(comments)} Comments`, contentX + 22, y);
    ctx.fillText('Share', contentX + 150, y);
    ctx.fillText('Save', contentX + 210, y);

    // Revoked on cleanup rather than inside the state updater: React may invoke an updater more
    // than once, and revoking there can kill the URL the anchor is about to use.
    let created: string | null = null;
    let cancelled = false;
    canvas.toBlob((blob) => {
      if (!blob || cancelled) return;
      created = URL.createObjectURL(blob);
      setDownloadUrl(created);
    }, 'image/png');

    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [subreddit, author, age, title, body, score, comments, mode]);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Reddit</span>
        <h2 className="tool-live-title">Fake Reddit post generator</h2>
        <p className="tool-live-subtitle">
          Write a post, pick the score, and download it as an image. Rendered entirely in your
          browser.
        </p>
      </div>

      <div className="tool-live-grid">
        {/* The panel is a single-column grid by default, which makes tool-live-field-full a no-op
            and stacks all eight controls into a very tall form. auto-fit collapses back to one
            column on a narrow viewport without needing a media query. */}
        <section
          className="tool-live-uploader"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}
        >
          <label className="tool-live-field tool-live-field-full">
            <span>Subreddit</span>
            <input
              maxLength={40}
              onChange={(e) => setSubreddit(e.target.value)}
              type="text"
              value={subreddit}
            />
          </label>

          <label className="tool-live-field">
            <span>Author</span>
            <input
              maxLength={32}
              onChange={(e) => setAuthor(e.target.value)}
              type="text"
              value={author}
            />
          </label>

          <label className="tool-live-field">
            <span>Posted</span>
            <input
              maxLength={20}
              onChange={(e) => setAge(e.target.value)}
              type="text"
              value={age}
            />
          </label>

          <label className="tool-live-field tool-live-field-full">
            <span>Title</span>
            <textarea onChange={(e) => setTitle(e.target.value)} rows={2} value={title} />
          </label>

          <label className="tool-live-field tool-live-field-full">
            <span>Body (optional)</span>
            <textarea onChange={(e) => setBody(e.target.value)} rows={4} value={body} />
          </label>

          <label className="tool-live-field">
            <span>Upvotes</span>
            <input
              max={999999}
              min={0}
              onChange={(e) => setScore(Number(e.target.value) || 0)}
              type="number"
              value={score}
            />
          </label>

          <label className="tool-live-field">
            <span>Comments</span>
            <input
              max={999999}
              min={0}
              onChange={(e) => setComments(Number(e.target.value) || 0)}
              type="number"
              value={comments}
            />
          </label>

          <label className="tool-live-field tool-live-field-full">
            <span>Theme</span>
            <select onChange={(e) => setMode(e.target.value as RedditTheme)} value={mode}>
              <option value="light">Light</option>
              <option value="dark">Dark</option>
            </select>
          </label>

          <div className="tool-live-actions tool-live-field-full">
            {downloadUrl ? (
              <a
                className="button button-secondary"
                download="fake-reddit-post.png"
                href={downloadUrl}
                // Fire-and-forget analytics only; see the same note in BrowserChatGenerator.tsx.
                onClick={() => {
                  void consumeToolUsage('fake-reddit-post-generator', '').catch(() => {});
                }}
              >
                Download PNG
              </a>
            ) : null}
          </div>
        </section>

        <section className="tool-live-preview">
          <canvas
            ref={canvasRef}
            style={{
              maxWidth: '100%',
              height: 'auto',
              borderRadius: 12,
              border: '1px solid rgba(0,0,0,0.1)',
            }}
          />
          <p className="tool-live-panel-note">
            Rendered locally with an HTML canvas. This is a mockup for design and fun.
          </p>
        </section>
      </div>
    </div>
  );
}
